import { useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { demoApi, problemFieldErrors, problemMessage } from "@/api/client";
import type { CreateReminderRequest, Equipment } from "@/api/types";
import { DialogAlert, EquipmentDialogShell } from "./EquipmentDialogShell";

type Field = "title" | "dueOn" | "dueHours" | "intervalDays" | "intervalHours" | "notes";
const FIELDS: readonly Field[] = ["title", "dueOn", "dueHours", "intervalDays", "intervalHours", "notes"];

/** `""` -> undefined, otherwise the number (NaN when it is not one — the caller refuses it). */
function figure(text: string): number | undefined {
  return text.trim() === "" ? undefined : Number(text);
}

/**
 * ef-061-8 — "Add reminder for {name}": What needs doing, Due on, Due at hours, Repeat every (days),
 * Repeat every (hours), Notes. A reminder needs a date or an hours figure to trigger on — neither is
 * refused inline ("Give a due date or a due-at-hours figure.") before anything is sent; every other rule
 * is the server's, shown from its own 400 with `aria-invalid` on the field it names.
 */
export function ReminderDialog({
  equipment,
  onClose,
  onSaved,
}: {
  equipment: Equipment;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState("");
  const [dueOn, setDueOn] = useState("");
  const [dueHours, setDueHours] = useState("");
  const [intervalDays, setIntervalDays] = useState("");
  const [intervalHours, setIntervalHours] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<ReadonlySet<string>>(new Set());
  const ids = { title: useId(), dueOn: useId(), dueHours: useId(), intervalDays: useId(), intervalHours: useId(), notes: useId() };
  const inFlight = useRef(false);

  const flag = (f: Field): true | undefined => (invalid.has(f) ? true : undefined);

  function refuse(field: Field | "trigger", message: string): void {
    setInvalid(new Set(field === "trigger" ? ["dueOn", "dueHours"] : [field]));
    setError(message);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return; // a double-click must not send a second request
    setError(null);
    setInvalid(new Set());

    const what = title.trim();
    if (what === "") return refuse("title", "What needs doing is required.");

    const hoursDue = figure(dueHours);
    const everyDays = figure(intervalDays);
    const everyHours = figure(intervalHours);
    if (dueOn === "" && hoursDue === undefined) {
      return refuse("trigger", "Give a due date or a due-at-hours figure.");
    }
    if (hoursDue !== undefined && !(Number.isFinite(hoursDue) && hoursDue >= 0)) {
      return refuse("dueHours", "Due at hours: enter the meter reading it falls due at.");
    }
    if (everyDays !== undefined && !(Number.isInteger(everyDays) && everyDays > 0)) {
      return refuse("intervalDays", "Repeat every (days): enter a whole number of days.");
    }
    if (everyHours !== undefined && !(Number.isFinite(everyHours) && everyHours > 0)) {
      return refuse("intervalHours", "Repeat every (hours): enter a number of hours above 0.");
    }

    const body: CreateReminderRequest = { title: what, equipmentId: equipment.id };
    if (dueOn !== "") body.dueOn = dueOn;
    if (hoursDue !== undefined) body.dueHours = hoursDue;
    if (everyDays !== undefined) body.intervalDays = everyDays;
    if (everyHours !== undefined) body.intervalHours = everyHours;
    if (notes.trim() !== "") body.notes = notes.trim();

    inFlight.current = true;
    setSaving(true);
    try {
      await demoApi.createReminder(body);
      onSaved();
      onClose();
    } catch (err) {
      const named = Object.keys(problemFieldErrors(err) ?? {})
        .map((k) => FIELDS.find((f) => f.toLowerCase() === k.toLowerCase()))
        .filter((f): f is Field => f !== undefined);
      setInvalid(new Set(named));
      setError(problemMessage(err, "Could not save this reminder."));
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  }

  const formId = useId();

  return (
    <EquipmentDialogShell
      title={`Add reminder for ${equipment.name}`}
      saving={saving}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {saving ? "Saving…" : "Save reminder"}
          </button>
        </>
      }
    >
      {({ alertId }) => {
        const describe = (f: Field): string | undefined => (invalid.has(f) ? alertId : undefined);
        return (
          <form id={formId} className="emp-dialog__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
            <div className="emp-dialog__field">
              <label htmlFor={ids.title}>What needs doing</label>
              <input
                id={ids.title}
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoComplete="off"
                aria-invalid={flag("title")}
                aria-describedby={describe("title")}
              />
            </div>
            <div className="equipment-dialog__row">
              <div className="emp-dialog__field">
                <label htmlFor={ids.dueOn}>Due on</label>
                <input
                  id={ids.dueOn}
                  type="date"
                  value={dueOn}
                  onChange={(e) => setDueOn(e.target.value)}
                  aria-invalid={flag("dueOn")}
                  aria-describedby={describe("dueOn")}
                />
              </div>
              <div className="emp-dialog__field">
                <label htmlFor={ids.dueHours}>Due at hours</label>
                <input
                  id={ids.dueHours}
                  type="number"
                  inputMode="decimal"
                  step="any"
                  min={0}
                  value={dueHours}
                  onChange={(e) => setDueHours(e.target.value)}
                  aria-invalid={flag("dueHours")}
                  aria-describedby={describe("dueHours")}
                />
              </div>
            </div>
            <div className="equipment-dialog__row">
              <div className="emp-dialog__field">
                <label htmlFor={ids.intervalDays}>Repeat every (days)</label>
                <input
                  id={ids.intervalDays}
                  type="number"
                  inputMode="numeric"
                  step={1}
                  min={1}
                  value={intervalDays}
                  onChange={(e) => setIntervalDays(e.target.value)}
                  aria-invalid={flag("intervalDays")}
                  aria-describedby={describe("intervalDays")}
                />
              </div>
              <div className="emp-dialog__field">
                <label htmlFor={ids.intervalHours}>Repeat every (hours)</label>
                <input
                  id={ids.intervalHours}
                  type="number"
                  inputMode="decimal"
                  step="any"
                  min={0}
                  value={intervalHours}
                  onChange={(e) => setIntervalHours(e.target.value)}
                  aria-invalid={flag("intervalHours")}
                  aria-describedby={describe("intervalHours")}
                />
              </div>
            </div>
            <div className="emp-dialog__field">
              <label htmlFor={ids.notes}>Notes</label>
              <textarea
                id={ids.notes}
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                aria-invalid={flag("notes")}
                aria-describedby={describe("notes")}
              />
            </div>

            <DialogAlert id={alertId} message={error} />
          </form>
        );
      }}
    </EquipmentDialogShell>
  );
}
